import { animate, scroll } from 'motion';
import { theme } from '@fiverr-private/theme';
import {
    brandPageTheme,
    HEADER_HEIGHT,
    SCROLL_SPEED_DECREASE_MULTIPLIER,
    SCROLL_SPEED_INCREASE_MULTIPLIER,
    SCROLL_SPEED_MULTIPLIER,
} from '../constants';
import { PRO_SECTION_CAROUSEL_GAPS } from '../../components/ProSection/constants';
import { endSizesForCarousel as endSizes, paddingsForCarousel as paddings, ResolutionsBasedKeys } from './constants';

export const calculateScrollSpeed = (speed: number) =>
    speed *
    SCROLL_SPEED_MULTIPLIER *
    (speed >= 1 ? SCROLL_SPEED_INCREASE_MULTIPLIER : SCROLL_SPEED_DECREASE_MULTIPLIER);

export const addScrollSpeedMotion = (
    scopeElement: HTMLElement = document.body,
    startPoint = 'start center' as const
) => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const animationCleanupMap = new Map<HTMLElement, () => void>();

    const applyAnimations = () => {
        scopeElement.querySelectorAll('[data-speed]').forEach((element) => {
            const el = element as HTMLElement;

            if (animationCleanupMap.has(el)) {
                return;
            }

            const speed = Number(el.dataset.speed);

            const animation = animate(el, {
                y: speed && !isNaN(speed) ? calculateScrollSpeed(speed) : 0,
            });
            scroll(animation, {
                target: el,
                offset: [startPoint, 'end center'],
            });

            animationCleanupMap.set(el, () => {
                animation.cancel();
                animation.stop();
            });
        });
    };

    const cancelAnimations = () => {
        animationCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        animationCleanupMap.clear();
    };

    const handleResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = animationCleanupMap.size > 0;

        if (shouldHaveAnimations && !hasAnimations) {
            applyAnimations();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelAnimations();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        applyAnimations();
    }

    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
        cancelAnimations();
    };
};

export const getResolution = (width: number) => {
    if (width >= Number(theme.numericBreakpoints.lg) && width < Number(theme.numericBreakpoints.xl)) {
        return theme.breakpoints.lg;
    }
    if (width >= Number(theme.numericBreakpoints.xl) && width < Number(brandPageTheme.numericBreakpoints.xxl)) {
        return theme.breakpoints.xl;
    }
    if (
        width >= Number(brandPageTheme.numericBreakpoints.xxl) &&
        width < Number(brandPageTheme.numericBreakpoints.xxxl)
    ) {
        return brandPageTheme.breakpoints.xxl;
    }
    if (width >= Number(brandPageTheme.numericBreakpoints.xxxl)) {
        return brandPageTheme.breakpoints.xxxl;
    }
    return 'default';
};

const getElementPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const position = {
        top: rect.top + scrollTop,
        left: rect.left,
    };

    return {
        x: position.left,
        y: position.top,
    };
};

const getTargetCarouselOffset = (element: HTMLElement, resolution: keyof typeof paddings = theme.breakpoints.xl) => {
    const targetCarousel = document.querySelector('[data-target-carousel]');

    if (!targetCarousel || !element) {
        return;
    }

    const targetPosition = getElementPosition(targetCarousel as HTMLElement);
    const elementPosition = getElementPosition(element as HTMLElement);

    return {
        x: targetPosition.x - elementPosition.x + paddings[resolution]?.paddingLeft,
        y: targetPosition.y - elementPosition.y + paddings[resolution]?.paddingTop,
    };
};

const getAncestorsSpeedCorrection = (element: HTMLElement) => {
    if (!element) {
        return 0;
    }

    let currentElement = element as HTMLElement | null;
    let speedCorrection = 0;

    while (!currentElement?.hasAttribute('data-scroll-wrapper') && currentElement) {
        const speed = Number(currentElement.dataset.speed);

        if (!isNaN(speed)) {
            speedCorrection += calculateScrollSpeed(speed);
        }

        currentElement = currentElement.parentElement;
    }

    return speedCorrection;
};

export const addFadeOutAnimations = () => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const fadeOutAnimationsCleanupMap = new Map<HTMLElement, () => void>();

    const applyFadeOutAnimations = () => {
        document.querySelectorAll('[data-scroll-fade-out="true"]').forEach((element) => {
            const el = element as HTMLElement;

            if (fadeOutAnimationsCleanupMap.has(el)) {
                return;
            }

            const animation = animate(
                el,
                {
                    opacity: 0,
                },
                { ease: 'easeInOut' }
            );

            scroll(animation, {
                target: document.querySelector('[data-scroll-content]') as HTMLElement,
                offset: [`start ${HEADER_HEIGHT}px`, 'end center'],
            });

            fadeOutAnimationsCleanupMap.set(el, () => {
                animation.cancel();
                animation.stop();
            });
        });
    };

    const cancelFadeOutAnimations = () => {
        fadeOutAnimationsCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        fadeOutAnimationsCleanupMap.clear();
    };

    const handleFadeOutResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = fadeOutAnimationsCleanupMap.size > 0;

        if (shouldHaveAnimations && !hasAnimations) {
            applyFadeOutAnimations();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelFadeOutAnimations();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        applyFadeOutAnimations();
    }

    window.addEventListener('resize', handleFadeOutResize);

    return () => {
        window.removeEventListener('resize', handleFadeOutResize);
        cancelFadeOutAnimations();
    };
};

export const addTransferToCarouselAnimation = () => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const transferToCarouselAnimationCleanupMap = new Map<HTMLElement, () => void>();

    const applyTransferToCarouselAnimation = () => {
        const resolution = getResolution(window.innerWidth);

        const transferToCarouselItems = document.querySelectorAll(
            '[data-transfer-to-carousel-item]'
        ) as NodeListOf<HTMLElement>;

        transferToCarouselItems.forEach((item) => {
            const itemOffset = getTargetCarouselOffset(item as HTMLElement, resolution as ResolutionsBasedKeys);

            const offsetX = itemOffset?.x;
            const offsetY = itemOffset?.y;

            if (offsetX === undefined || offsetY === undefined) {
                return;
            }

            const animation = animate(
                item,
                {
                    x: offsetX,
                    y: offsetY - getAncestorsSpeedCorrection(item),
                    width: endSizes[resolution]?.width,
                    height: endSizes[resolution]?.height,
                },
                { ease: 'easeInOut' }
            );

            scroll(animation, {
                target: item,
                offset: ['start center', `${offsetY - 250}px`],
            });

            transferToCarouselAnimationCleanupMap.set(item, () => {
                animation.stop();
                animation.cancel();
                setTimeout(() => {
                    item.style.removeProperty('transform');
                    item.style.width = '100%';
                    item.style.height = 'auto';
                    item.style.display = 'block';
                }, 0);
            });
        });
    };

    const cancelTransferToCarouselAnimation = () => {
        if (transferToCarouselAnimationCleanupMap.size) {
            transferToCarouselAnimationCleanupMap.forEach((cleanup) => {
                cleanup();
            });
            transferToCarouselAnimationCleanupMap.clear();
        }
    };

    const handleTransferToCarouselResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = transferToCarouselAnimationCleanupMap.size !== 0;

        if (shouldHaveAnimations && !hasAnimations) {
            document.documentElement.scrollTop = 0;
            applyTransferToCarouselAnimation();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelTransferToCarouselAnimation();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        document.documentElement.scrollTop = 0;
        applyTransferToCarouselAnimation();
    }

    window.addEventListener('resize', handleTransferToCarouselResize);

    return () => {
        window.removeEventListener('resize', handleTransferToCarouselResize);
        cancelTransferToCarouselAnimation();
    };
};

export const addFadeInAnimation = (container: HTMLElement) => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const fadeInAnimationsCleanupMap = new Map<HTMLElement, () => void>();

    const applyFadeInAnimations = () => {
        const el = document.querySelector('[data-scroll-fade-in]') as HTMLElement;

        if (fadeInAnimationsCleanupMap.has(el)) {
            return;
        }

        const animation = animate(el, { opacity: [0, 1] });

        scroll(animation, {
            target: container,
            offset: ['start center', `start ${HEADER_HEIGHT}px`],
        });

        fadeInAnimationsCleanupMap.set(el, () => {
            animation.cancel();
            animation.stop();
        });
    };

    const cancelFadeInAnimations = () => {
        fadeInAnimationsCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        fadeInAnimationsCleanupMap.clear();
    };

    const handleFadeInResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = fadeInAnimationsCleanupMap.size > 0;

        if (shouldHaveAnimations && !hasAnimations) {
            applyFadeInAnimations();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelFadeInAnimations();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        applyFadeInAnimations();
    }

    window.addEventListener('resize', handleFadeInResize);

    return () => {
        window.removeEventListener('resize', handleFadeInResize);
        cancelFadeInAnimations();
    };
};

const correctionCoefficient = 0.04;

export const addVanishAnimation = (container: HTMLElement) => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const vanishAnimationsCleanupMap = new Map<HTMLElement, () => void>();
    const descriptionSections = document.querySelectorAll('[data-description-section]') as NodeListOf<HTMLElement>;

    const applyVanishAnimations = () => {
        const sectionHeight = descriptionSections[descriptionSections.length - 1].offsetHeight;

        container.style.height = `${sectionHeight * (descriptionSections.length + 1)}px`;

        const correction = correctionCoefficient * descriptionSections.length;

        descriptionSections.forEach((section, index) => {
            if (index === descriptionSections.length - 1) {
                return;
            }

            if (vanishAnimationsCleanupMap.has(section)) {
                return;
            }

            let canceled = false;

            const animation = (progress: number) => {
                if (canceled) {
                    return;
                }

                if (progress === 1) {
                    section.style.removeProperty('display');
                    section.classList.add('vanish');
                } else {
                    section.style.setProperty('display', 'flex');
                    setTimeout(() => {
                        section.classList.remove('vanish');
                    }, 0);
                }
            };

            vanishAnimationsCleanupMap.set(section, () => {
                canceled = true;
            });

            scroll(animation, {
                target: container as HTMLElement,
                offset: [
                    `start ${HEADER_HEIGHT}px`,
                    `${sectionHeight * (index + (1 - correction))}px ${HEADER_HEIGHT}px`,
                ],
            });
        });
    };

    const cancelVanishAnimations = () => {
        vanishAnimationsCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        vanishAnimationsCleanupMap.clear();
        container.style.height = 'fit-content';
    };

    const handleVanishResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = vanishAnimationsCleanupMap.size > 0;

        if (shouldHaveAnimations && !hasAnimations) {
            applyVanishAnimations();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelVanishAnimations();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        applyVanishAnimations();
    }

    window.addEventListener('resize', handleVanishResize);

    return () => {
        window.removeEventListener('resize', handleVanishResize);
        cancelVanishAnimations();
    };
};

export const addProSectionCarouselAnimation = (container: HTMLElement, isV16 = false) => {
    const lgBreakpoint = Number(theme.numericBreakpoints.lg);
    const scrollRotatorItems = document.querySelectorAll(
        '[data-scroll-rotator-item="true"]'
    ) as NodeListOf<HTMLElement>;
    const scrollRotator = scrollRotatorItems[0].parentElement as HTMLElement;
    const descriptionSections = document.querySelectorAll('[data-description-section]') as NodeListOf<HTMLElement>;
    const proSectionCarouselAnimationsCleanupMap = new Map<HTMLElement, () => void>();
    const isThreeOrLessItems = scrollRotatorItems.length <= 3;

    // Use a smaller correction coefficient for fewer elements to make animation smoother
    const proSectionCorrectionCoefficient = scrollRotatorItems.length <= 3 ? 0.02 : correctionCoefficient;
    const correction = proSectionCorrectionCoefficient * scrollRotatorItems.length;

    // Adjust white space reduction based on element count
    const whiteSpaceReduction = scrollRotatorItems.length <= 3 ? 0.7 : 0.5;

    const applyProSectionCarouselAnimations = () => {
        const sectionHeight = descriptionSections[descriptionSections.length - 1].offsetHeight;
        const startOffset = `start ${HEADER_HEIGHT}px` as const;
        // Reduce the end offset to make "Popular services" appear earlier and reduce white space
        const endOffset = isThreeOrLessItems
            ? (`${sectionHeight * (scrollRotatorItems.length - whiteSpaceReduction)}px ${HEADER_HEIGHT}px` as const)
            : (`${sectionHeight * scrollRotatorItems.length}px ${HEADER_HEIGHT}px` as const);

        const offset = [startOffset, endOffset];

        const gap = PRO_SECTION_CAROUSEL_GAPS[getResolution(window.innerWidth)];

        scrollRotatorItems.forEach((item, index) => {
            let canceled = false;

            if (proSectionCarouselAnimationsCleanupMap.has(item)) {
                return;
            }

            if (index === 0) {
                const transferToCarouselItems = document.querySelectorAll(
                    '[data-transfer-to-carousel-item]'
                ) as NodeListOf<HTMLElement>;

                const animation = (progress: number) => {
                    if (canceled) {
                        return;
                    }

                    if (progress > 0) {
                        transferToCarouselItems.forEach((item) => {
                            item.style.display = 'none';
                        });

                        if (!isV16) {
                            item.classList.remove('first');
                        }
                    } else {
                        transferToCarouselItems.forEach((item) => {
                            item.style.display = 'block';
                        });

                        if (!isV16) {
                            item.classList.add('first');
                        }
                    }
                };

                scroll(animation, {
                    target: container,
                    offset: [`start ${HEADER_HEIGHT}px`, `start ${HEADER_HEIGHT}px`],
                });

                transferToCarouselItems.forEach((item) => {
                    proSectionCarouselAnimationsCleanupMap.set(item, () => {
                        item.style.display = 'block';
                    });
                });
            }

            const sectionRange = 1 / scrollRotatorItems.length;

            const animation = (progress: number) => {
                if (canceled) {
                    return;
                }

                const snap = Math.floor(Math.floor((progress / sectionRange) * 10) / 10);

                const snapPosition = snap === scrollRotatorItems.length ? scrollRotatorItems.length - 1 : snap;

                if (
                    progress >= sectionRange * index - proSectionCorrectionCoefficient &&
                    progress < sectionRange * (index + 1) - proSectionCorrectionCoefficient
                ) {
                    item.classList.remove('not-active');
                } else if (progress >= 1 - proSectionCorrectionCoefficient && index === scrollRotatorItems.length - 1) {
                    item.classList.remove('not-active');
                } else {
                    item.classList.add('not-active');
                }

                scrollRotator.style.setProperty(
                    '--rotator-z-index',
                    `${scrollRotatorItems.length - (snapPosition + 1)}`
                );
            };

            scroll(animation, { target: container, offset });

            const freeScrollAnimation = animate(
                item,
                {
                    transform: `translateX(-${(item.offsetWidth + gap) * (scrollRotatorItems.length - 1)}px)`,
                },
                {
                    ease: 'linear',
                }
            );

            scroll(freeScrollAnimation, {
                target: container,
                offset: [
                    `start ${HEADER_HEIGHT}px`,
                    isThreeOrLessItems
                        ? `${sectionHeight * (scrollRotatorItems.length - whiteSpaceReduction)}px ${HEADER_HEIGHT}px`
                        : `${sectionHeight * (scrollRotatorItems.length - 1)}px ${HEADER_HEIGHT}px`,
                ],
            });

            let scaleArray = [0.8, 1, 1, 0.8];
            let offsetArray = [
                `${sectionHeight * (index - (1 + correction))}px ${HEADER_HEIGHT}px` as const,
                `${sectionHeight * (index - correction)}px ${HEADER_HEIGHT}px` as const,
                `${sectionHeight * (index + 1 - correction)}px ${HEADER_HEIGHT}px` as const,
                `${sectionHeight * (index + 1)}px ${HEADER_HEIGHT}px` as const,
            ];

            if (index === 0) {
                scaleArray = [1, 0.8];
                offsetArray = [
                    `${sectionHeight * (index + 1 - correction)}px ${HEADER_HEIGHT}px` as const,
                    `${sectionHeight * (index + 1)}px ${HEADER_HEIGHT}px` as const,
                ];
            }

            if (index === scrollRotatorItems.length - 1) {
                scaleArray = [0.8, 1];
                offsetArray = [
                    `${sectionHeight * (index - (1 + correction))}px ${HEADER_HEIGHT}px` as const,
                    `${sectionHeight * (index - correction)}px ${HEADER_HEIGHT}px` as const,
                ];
            }

            const scaleAnimation = animate(
                item.querySelector('img') as HTMLElement,
                {
                    scale: scaleArray,
                },
                {
                    ease: 'linear',
                }
            );

            scroll(scaleAnimation, {
                target: container,
                offset: offsetArray,
            });

            proSectionCarouselAnimationsCleanupMap.set(item, () => {
                freeScrollAnimation.stop();
                freeScrollAnimation.cancel();

                if (scaleAnimation) {
                    scaleAnimation.stop();
                    scaleAnimation.cancel();
                }

                canceled = true;
                item.style.removeProperty('transform');
                if (index !== 0) {
                    item.classList.add('not-active');
                } else {
                    item.classList.remove('not-active');
                    item.classList.add('first');
                }
            });
        });
    };

    const cancelProSectionCarouselAnimations = () => {
        proSectionCarouselAnimationsCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        proSectionCarouselAnimationsCleanupMap.clear();
    };

    const handleProSectionCarouselResize = () => {
        const shouldHaveAnimations = window.innerWidth >= lgBreakpoint;
        const hasAnimations = proSectionCarouselAnimationsCleanupMap.size > 0;

        if (shouldHaveAnimations && !hasAnimations) {
            applyProSectionCarouselAnimations();
        } else if (!shouldHaveAnimations && hasAnimations) {
            cancelProSectionCarouselAnimations();
        }
    };

    if (window.innerWidth >= lgBreakpoint) {
        applyProSectionCarouselAnimations();
    }

    window.addEventListener('resize', handleProSectionCarouselResize);

    return () => {
        window.removeEventListener('resize', handleProSectionCarouselResize);
        cancelProSectionCarouselAnimations();
    };
};
